import {
  AlertCircle,
  AlertTriangle,
  Check,
  HelpCircle,
  ListChecks,
  Pencil,
  Plus,
  Search,
  X,
} from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { isHiringManagerOnly } from '@/modules/auth/utils/hasRoles';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { useSkillLintMutation } from '@/modules/jobs/mutations/useSkillLintMutation';
import { useTitleLintMutation } from '@/modules/jobs/mutations/useTitleLintMutation';
import { useJobSignalQuery } from '@/modules/jobs/queries/useJobSignalQuery';
import { useSkillSearchQuery } from '@/modules/jobs/queries/useSkillSearchQuery';
import {
  completenessLabelText,
  deriveNeeds,
  nextStepCopy,
  skillSufficiencyMessage,
  toScreeningQuestion,
} from '@/modules/jobs/signal/derivations';
import { displayLabel, normalize } from '@/modules/jobs/signal/displayCasing';
import { emphasisOf, withEmphasis } from '@/modules/jobs/signal/emphasis';
import {
  missingForSubmit,
  type SubmitIntent,
} from '@/modules/jobs/signal/jobRequestMappers';
import { hasOfficeWork } from '@/modules/jobs/signal/location';
import {
  type Completeness,
  contractTypeOptions,
  type EmphasisLevel,
  emptyRole,
  emptyWorkflow,
  isJobContractType,
  isJobLocationType,
  type JobSignal,
  locationTypeOptions,
  type NeedItem,
  type RoleDraft,
  seniorityOptions,
  type SkillVerdict,
  type SufficiencyLevel,
  type TitleVerdict,
  type WorkflowDraft,
} from '@/modules/jobs/signal/types';
import {
  isStrongVerdict,
  verdictElicitation,
} from '@/modules/jobs/signal/verdict';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { Input } from '@/modules/ui/components/input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';
import { MultiSelect, type MultiSelectRef } from '@/modules/ui/MultiSelect';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { ExampleCompaniesNote } from './ExampleCompaniesNote';
import { JobCountriesField } from './JobCountriesField';
import { JobEmployerIndustriesField } from './JobEmployerIndustriesField';
import { JobEmployerSizeField } from './JobEmployerSizeField';
import { JobSearchTitlesField } from './JobSearchTitlesField';
import { JobSlackChannelField } from './JobSlackChannelField';
import { SkillEmphasisMenu } from './SkillEmphasisMenu';
import { SkillTypeahead } from './SkillTypeahead';
import { verdictChipClass, verdictDot } from './tone';
import { WorkflowSettingsCard } from './WorkflowSettingsCard';

export interface JobSignalPanelProps {
  /** Seed the screen in a particular state (for stories). */
  initialRole?: Partial<RoleDraft>;
  /** Seed the lint verdict cache (for stories — production fills it from `/skill-lint`). */
  initialVerdicts?: SkillVerdict[];
  /** Seed the title verdict (for stories — production fills it from `/title-lint` on blur). */
  initialTitleVerdict?: TitleVerdict;
  /** Seed the job-signal result (for stories — production fills it from `/ai/job-signal`). */
  initialJobSignal?: JobSignal;
  /** Open one skill's Fix popover on mount (for the ElicitationOpen story). */
  initialElicitationSkill?: string;
  /**
   * Receives the validated drafts on Save / Publish — the PAGE owns the wire mapping (`toJobCreateRequest` /
   * `toJobUpdateRequest`), mutation, success toast and navigation. Absent (stories) → the buttons are disabled.
   */
  onSubmit?: (
    role: RoleDraft,
    workflow: WorkflowDraft,
    intent: SubmitIntent,
  ) => void;
  /** disables Save/Publish while the page's mutation is in flight */
  submitting?: boolean;
  /** The page's last save or publish failure; shown under the header until the next attempt. */
  submitError?: string | null;
  /**
   * `edit` (plan 13b/13g/13h): relabels the buttons/header, seeds state from {@link initialWorkflow}, escalates
   * published-job saves to publish-level validation, disables the create-only owner/company derivations, and
   * switches the workflow card into edit mode (lifecycle & outreach chunk; no company/public-list controls).
   */
  mode?: 'create' | 'edit';
  /** edit-mode workflow seed, mapped from the loaded job (`jobToWorkflowDraft`) */
  initialWorkflow?: WorkflowDraft;
  /** hides Publish (edit of an already-published job — plan 13b: Publish is Draft-only) */
  canPublish?: boolean;
  /** locks the card's coordination controls — the backend rejects changes once a process exists (plan 13h) */
  hasCoordinationProcess?: boolean;
  /**
   * The job's ORIGINAL strategy when editing a legacy (non-Agent) job — enables the card's revertible
   * migration select and the save-time confirmation (the migration is permanent and auto-starts sourcing).
   */
  legacySourcingStrategy?: NonNullable<WorkflowDraft['sourcingStrategy']>;
}

type ResolveAction = 'replace' | 'toQuestion';
type SkillLintStatus = 'pending' | 'failed';

const SKILL_LINT_BATCH_SIZE = 50;
const SKILL_LINT_MAX_ITEM_LENGTH = 200;
const isSkillLintTermTooLong = (skill: string): boolean =>
  Array.from(skill).length > SKILL_LINT_MAX_ITEM_LENGTH;

const dedupePush = (arr: string[], value: string): string[] =>
  arr.some((x) => normalize(x) === normalize(value)) ? arr : [...arr, value];

const byInput = (verdicts: SkillVerdict[]): Record<string, SkillVerdict> =>
  Object.fromEntries(verdicts.map((v) => [normalize(v.input), v]));

export function JobSignalPanel({
  initialRole,
  initialVerdicts = [],
  initialTitleVerdict,
  initialJobSignal,
  initialElicitationSkill,
  onSubmit,
  submitting = false,
  submitError = null,
  mode = 'create',
  initialWorkflow,
  canPublish = true,
  hasCoordinationProcess = false,
  legacySourcingStrategy,
}: JobSignalPanelProps) {
  const [role, setRole] = useState<RoleDraft>({ ...emptyRole, ...initialRole });
  const [workflow, setWorkflow] = useState<WorkflowDraft>(
    initialWorkflow ?? emptyWorkflow,
  );
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [confirmMigrationOpen, setConfirmMigrationOpen] = useState(false);
  // The example companies as last committed (on blur), which their note checks: a model call, so never per keystroke.
  const [checkedCompanies, setCheckedCompanies] = useState('');
  const [elicitSkill, setElicitSkill] = useState<string | null>(
    initialElicitationSkill ?? null,
  );
  const [verdictCache, setVerdictCache] = useState<
    Record<string, SkillVerdict>
  >(() => byInput(initialVerdicts));
  const [skillLintStatus, setSkillLintStatus] = useState<
    Record<string, SkillLintStatus>
  >({});
  const [titleVerdictCache, setTitleVerdictCache] = useState<
    Record<string, TitleVerdict>
  >(() =>
    initialTitleVerdict && initialRole?.title
      ? { [normalize(initialRole.title)]: initialTitleVerdict }
      : {},
  );
  const lint = useSkillLintMutation();
  const titleLint = useTitleLintMutation();
  // normalized titles with an in-flight lint — dedupes repeat blurs of the same title before its verdict lands
  // (each lint is a billable LLM call; the cache only fills on success, so it can't guard the in-flight window).
  const inFlightTitles = useRef<Set<string>>(new Set());

  const titleRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const seniorityRef = useRef<MultiSelectRef>(null);
  const companiesRef = useRef<HTMLInputElement>(null);

  // Live verdicts and request state for the current must-have list, resolved by normalized skill.
  const verdicts = useMemo(
    () =>
      role.mustHave
        .map((s) => verdictCache[normalize(s)])
        .filter((v): v is SkillVerdict => Boolean(v)),
    [role.mustHave, verdictCache],
  );
  const pendingCount = role.mustHave.filter(
    (skill) => skillLintStatus[normalize(skill)] === 'pending',
  ).length;
  const failedSkills = role.mustHave.filter(
    (skill) => skillLintStatus[normalize(skill)] === 'failed',
  );
  const titleVerdict = titleVerdictCache[normalize(role.title)] ?? null;
  const needs = useMemo(
    () => deriveNeeds(role, titleVerdict),
    [role, titleVerdict],
  );
  const jobSignalInputs = useMemo(
    () => ({
      title: role.title,
      titleVerdict,
      city: role.location,
      seniority: role.seniority,
      skillVerdicts: verdicts.map((v) => v.verdict),
    }),
    [role.title, titleVerdict, role.location, role.seniority, verdicts],
  );
  const jobSignalQuery = useJobSignalQuery(jobSignalInputs, initialJobSignal);
  const jobSignal = jobSignalQuery.data;
  const completeness = jobSignal?.completeness;
  const sufficiencyLevel: SufficiencyLevel =
    jobSignal?.skillSufficiency.level ?? 'none';

  const patch = (change: Partial<RoleDraft>) =>
    setRole((r) => ({ ...r, ...change }));
  const patchWorkflow = (change: Partial<WorkflowDraft>) =>
    setWorkflow((w) => ({ ...w, ...change }));

  /* ── persona split (plan 13): customers see job attributes only; internal recruiters (elevated roles) also get
     the Workflow-settings card. Workflow values customers never see are derived at render, not stored: owner
     defaults to the current user and company to the (single) tenant unless the recruiter picked otherwise in the
     card. Multi-tenant recruiters must pick the company there. ── */
  const currentUserQuery = useCurrentUserQuery();
  const companiesQuery = useCompaniesQuery();
  const isRecruiterVariant =
    currentUserQuery.data != null &&
    !isHiringManagerOnly(currentUserQuery.data.roles ?? []);
  const companyOptions = (companiesQuery.data ?? [])
    .filter((c): c is typeof c & { name: string } => !!c.name)
    .map((c) => ({ value: c.id, label: c.name }));
  // derived from the same filtered list the picker shows, so auto-select and picker visibility can't disagree
  const singleCompanyId =
    companyOptions.length === 1 ? companyOptions[0]?.value : undefined;
  // The owner/company fallbacks are CREATE-only derivations. In edit mode the workflow is seeded from the loaded
  // job and must stay faithful to it — falling back to the current user there would silently claim an ownerless
  // job for whoever saves it (`toJobUpdateRequest` already preserves the stored owner via `|| job.ownerId`).
  const effectiveWorkflow: WorkflowDraft =
    mode === 'edit'
      ? workflow
      : {
          ...workflow,
          companyId: workflow.companyId || singleCompanyId || '',
          ownerId: workflow.ownerId || currentUserQuery.data?.id || '',
        };
  // A new job follows its company's employer size suggestion until someone picks a band. The companies list carries
  // it, so it fits whichever company the job is for, however late the list loads. A saved job has its own band.
  const suggestedEmployerSize =
    mode === 'create'
      ? companiesQuery.data?.find((c) => c.id === effectiveWorkflow.companyId)
          ?.suggestedEmployerSize
      : undefined;
  const employerSize = role.employerSize ?? suggestedEmployerSize ?? 'Any';
  // Its industries follow the company's own industry the same way.
  const suggestedEmployerIndustries =
    mode === 'create'
      ? (companiesQuery.data?.find((c) => c.id === effectiveWorkflow.companyId)
          ?.suggestedEmployerIndustries ?? undefined)
      : undefined;
  const employerIndustries =
    role.employerIndustries ?? suggestedEmployerIndustries ?? [];
  const submittedRole: RoleDraft = {
    ...role,
    employerSize,
    employerIndustries,
  };
  // Save/Publish wait for the company derivation: before /companies resolves a submit would fail with a
  // "Missing: company" toast while no company control is even on screen. Create-only — on edit the company is
  // immutable (the update request has no companyId) and already seeded from the job, so the gate would only
  // let a failing /companies call brick the edit screen.
  const companyLoadFailed =
    mode === 'create' &&
    companiesQuery.isError &&
    companiesQuery.data === undefined;
  const submitBlocked =
    !onSubmit ||
    submitting ||
    (mode === 'create' && companiesQuery.isPending) ||
    companyLoadFailed;

  const submit = (intent: SubmitIntent) => {
    if (!onSubmit) return;
    // Editing an already-published job validates at PUBLISH level even for "Save changes": its save-level
    // check (title+company) would let a cleared description/must-haves go through — and the backend re-grounds
    // on an emptied description, wiping the grounding the live search depends on.
    const validationIntent: SubmitIntent =
      mode === 'edit' && !canPublish ? 'publish' : intent;
    const missing = missingForSubmit(role, effectiveWorkflow, validationIntent);
    if (missing.length > 0) {
      toast.error(`Missing: ${missing.join(', ')}`);
      if (missing.includes('title')) titleRef.current?.focus();
      else if (missing.includes('location')) locationRef.current?.focus();
      return;
    }
    // Publishing is the moment of consequence for a customer (the search starts) — confirm first (plan 13a).
    // Recruiters know the machinery and skip the dialog.
    if (intent === 'publish' && !isRecruiterVariant) {
      setConfirmPublishOpen(true);
      return;
    }
    // A pending legacy→Agent migration is permanent once saved AND the backend auto-starts a sourcing run on
    // the transition — confirm before committing it (plan 13h).
    if (
      legacySourcingStrategy &&
      effectiveWorkflow.sourcingStrategy === 'Agent'
    ) {
      setConfirmMigrationOpen(true);
      return;
    }
    onSubmit(submittedRole, effectiveWorkflow, intent);
  };

  /** Lint only the skills we don't already have a verdict for (skill-commit, not per keystroke). */
  const lintSkills = (skills: string[]) => {
    const seen = new Set<string>();
    const fresh = skills
      .map((skill) => skill.trim())
      .filter((skill) => {
        const key = normalize(skill);
        if (
          !skill ||
          seen.has(key) ||
          verdictCache[key] ||
          skillLintStatus[key] === 'pending'
        ) {
          return false;
        }
        seen.add(key);
        return true;
      });
    if (fresh.length === 0) return;

    const tooLong = fresh.filter(isSkillLintTermTooLong);
    if (tooLong.length > 0) {
      setVerdictCache((cache) => ({
        ...cache,
        ...byInput(
          tooLong.map((input) => ({
            input,
            canonical: input,
            verdict: 'unknown',
          })),
        ),
      }));
      setSkillLintStatus((current) => {
        const next = { ...current };
        tooLong.forEach((skill) => delete next[normalize(skill)]);
        return next;
      });
      toast.error(
        `Skills must be ${SKILL_LINT_MAX_ITEM_LENGTH} characters or fewer. Shorten the highlighted skill.`,
      );
    }

    const lintable = fresh.filter((skill) => !isSkillLintTermTooLong(skill));
    const lintableKeys = lintable.map(normalize);
    setSkillLintStatus((current) => ({
      ...current,
      ...Object.fromEntries(lintableKeys.map((key) => [key, 'pending'])),
    }));

    const runBatches = async () => {
      for (
        let start = 0;
        start < lintable.length;
        start += SKILL_LINT_BATCH_SIZE
      ) {
        const batch = lintable.slice(start, start + SKILL_LINT_BATCH_SIZE);
        const batchKeys = batch.map(normalize);

        try {
          const results = await lint.mutateAsync(batch);
          const resultsByInput = byInput(results);
          setVerdictCache((cache) => ({ ...cache, ...resultsByInput }));
          setSkillLintStatus((current) => {
            const next = { ...current };
            batchKeys.forEach((key) => {
              if (resultsByInput[key]) delete next[key];
              else next[key] = 'failed';
            });
            return next;
          });
        } catch {
          setSkillLintStatus((current) => ({
            ...current,
            ...Object.fromEntries(batchKeys.map((key) => [key, 'failed'])),
          }));
          toast.error("Couldn't check some skills right now.");
        }
      }
    };
    void runBatches();
  };

  // Lint-on-load (plan 13b): a loaded job's must-haves arrive verdict-less — bounded, billable skill-lint batches
  // on mount make the dots live. Ref-guarded so it fires exactly once (lintSkills itself skips cached verdicts).
  // Gated on onSubmit so stories (no persistence wiring, no backend) never fire the call on mount.
  const didInitialLint = useRef(false);
  useEffect(() => {
    if (didInitialLint.current || !onSubmit) return;
    didInitialLint.current = true;
    if (role.mustHave.length > 0) {
      // lintSkills records pending state; defer it so the effect does not synchronously cascade a second render.
      queueMicrotask(() => lintSkills(role.mustHave));
    }
  });

  /** Lint the title on commit (blur): skip an empty, already-classified, or in-flight title (billable LLM call). */
  const lintTitle = (raw: string) => {
    const title = raw.trim();
    const key = normalize(title);
    if (!title || titleVerdictCache[key] || inFlightTitles.current.has(key)) {
      return;
    }
    inFlightTitles.current.add(key);
    void titleLint
      .mutateAsync(title)
      .then((verdict) =>
        setTitleVerdictCache((cache) => ({ ...cache, [key]: verdict })),
      )
      .catch(() => toast.error("Couldn't check the title right now."))
      .finally(() => inFlightTitles.current.delete(key));
  };

  const addSkill = (raw: string) => {
    const skill = raw.trim();
    if (!skill) return;
    if (isSkillLintTermTooLong(skill)) {
      toast.error(
        `Skills must be ${SKILL_LINT_MAX_ITEM_LENGTH} characters or fewer.`,
      );
      return;
    }
    setRole((r) => ({ ...r, mustHave: dedupePush(r.mustHave, skill) }));
    lintSkills([skill]);
  };

  const addNice = (raw: string) => {
    const skill = raw.trim();
    if (!skill) return;
    setRole((r) => ({ ...r, niceToHave: dedupePush(r.niceToHave, skill) }));
  };

  const removeSkill = (input: string) =>
    setRole((r) => ({
      ...r,
      mustHave: r.mustHave.filter((s) => s !== input),
      skillEmphasis: withEmphasis(r.skillEmphasis, input, 'Normal'),
    }));
  const setEmphasis = (skill: string, level: EmphasisLevel | 'Normal') =>
    setRole((r) => ({
      ...r,
      skillEmphasis: withEmphasis(r.skillEmphasis, skill, level),
    }));

  const resolveSkill = (
    verdict: SkillVerdict,
    action: ResolveAction,
    value: string,
  ) => {
    const answer = value.trim();
    if (!answer) return;
    if (action === 'replace' && isSkillLintTermTooLong(answer)) {
      toast.error(
        `Skills must be ${SKILL_LINT_MAX_ITEM_LENGTH} characters or fewer.`,
      );
      return;
    }

    setRole((r) => {
      const mustHave = r.mustHave.filter(
        (s) => normalize(s) !== normalize(verdict.input),
      );
      if (action === 'replace') {
        return { ...r, mustHave: dedupePush(mustHave, answer) };
      }
      return {
        ...r,
        mustHave,
        screeningQuestions: dedupePush(
          r.screeningQuestions,
          toScreeningQuestion(answer),
        ),
      };
    });
    if (action === 'replace') lintSkills([answer]);
    setElicitSkill(null);
  };

  const removeQuestion = (index: number) =>
    setRole((r) => ({
      ...r,
      screeningQuestions: r.screeningQuestions.filter((_, i) => i !== index),
    }));

  const saveQuestion = (index: number, text: string) =>
    setRole((r) => ({
      ...r,
      screeningQuestions: r.screeningQuestions.map((q, i) =>
        i === index ? text : q,
      ),
    }));

  const focusNeed = (id: NeedItem['id']) => {
    if (id === 'seniority') {
      // MultiSelect exposes focus() only; focusing its trigger scrolls it into view.
      seniorityRef.current?.focus();
      return;
    }
    const el =
      id === 'title'
        ? titleRef.current
        : id === 'location'
          ? locationRef.current
          : id === 'companies'
            ? companiesRef.current
            : null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus();
  };

  return (
    <div className="bg-neutral-120 min-h-full font-sans">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <header className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-primary-700 text-2xl font-medium">
              {mode === 'edit' ? 'Edit role' : 'New role'}
            </h1>
            <p className="text-muted-foreground text-sm">
              Describe the role — we&apos;ll tell you what we still need and how
              searchable your skills are, as you go.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <PermissionsGate
              permissions={[
                mode === 'create'
                  ? permissions.Jobs.Create
                  : permissions.Jobs.Update,
              ]}
            >
              <Button
                variant="outline"
                size="medium"
                onClick={() => submit('save')}
                disabled={submitBlocked}
              >
                {mode === 'edit' ? 'Save changes' : 'Save draft'}
              </Button>
            </PermissionsGate>
            {canPublish ? (
              <PermissionsGate
                permissions={
                  mode === 'create'
                    ? [permissions.Jobs.Create]
                    : [permissions.Jobs.Update, permissions.Jobs.StartSourcing]
                }
              >
                <Button
                  size="medium"
                  onClick={() => submit('publish')}
                  disabled={submitBlocked}
                >
                  Publish
                </Button>
              </PermissionsGate>
            ) : null}
          </div>
        </header>

        {submitError ? (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}

        {companyLoadFailed ? (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle />
            <AlertDescription className="gap-3">
              <span>
                Companies could not be loaded. Try again before saving this
                role.
              </span>
              <Button
                size="sm"
                variant="outline"
                loading={companiesQuery.isFetching}
                onClick={() => void companiesQuery.refetch()}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        <ConfirmDialog
          open={confirmPublishOpen}
          onOpenChange={setConfirmPublishOpen}
          title="Ready to publish?"
          description="Publishing starts the candidate search immediately. You can still edit the role afterwards — edits shape upcoming interviews and messages, not the finished search."
          confirmText="Publish"
          onConfirm={() =>
            onSubmit?.(submittedRole, effectiveWorkflow, 'publish')
          }
        />

        <ConfirmDialog
          open={confirmMigrationOpen}
          onOpenChange={setConfirmMigrationOpen}
          title="Switch to Sourcing Agent?"
          description="Switching this legacy job to the Sourcing Agent is permanent and immediately starts an agent search when saved. Your other changes are saved at the same time."
          confirmText="Switch & save"
          onConfirm={() => onSubmit?.(submittedRole, effectiveWorkflow, 'save')}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
          {/* ── Left: the role (+ recruiter-only workflow settings) ───────── */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>The role</CardTitle>
                <CardDescription>
                  What you give us here becomes how we search and interview.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Field label="Title">
                  <Input
                    ref={titleRef}
                    autoFocus={mode === 'create'}
                    placeholder="Senior Backend Engineer"
                    value={role.title}
                    onChange={(e) => patch({ title: e.target.value })}
                    onBlur={(e) => lintTitle(e.target.value)}
                  />
                </Field>

                <Field label="Description">
                  <Textarea
                    placeholder="What the person will own, the team, the stack, what 'good' looks like…"
                    rows={7}
                    value={role.description}
                    onChange={(e) => patch({ description: e.target.value })}
                  />
                </Field>

                <Field label="Must-have skills">
                  <SkillSearchInput
                    onCommit={addSkill}
                    placeholder="Add a must-have skill"
                  />
                  <SkillChips
                    skills={role.mustHave}
                    verdictCache={verdictCache}
                    onRemove={removeSkill}
                    emphasis={role.skillEmphasis}
                    onEmphasis={setEmphasis}
                  />
                </Field>

                <Field label="Nice-to-have skills">
                  <SkillSearchInput
                    onCommit={addNice}
                    placeholder="Add a nice-to-have skill"
                  />
                  <SkillChips
                    skills={role.niceToHave}
                    onRemove={(s) =>
                      patch({
                        niceToHave: role.niceToHave.filter((x) => x !== s),
                        skillEmphasis: withEmphasis(
                          role.skillEmphasis,
                          s,
                          'Normal',
                        ),
                      })
                    }
                    emphasis={role.skillEmphasis}
                    onEmphasis={setEmphasis}
                  />
                </Field>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Seniority">
                    <MultiSelect
                      ref={seniorityRef}
                      options={seniorityOptions}
                      defaultValue={role.seniority}
                      onValueChange={(v) => patch({ seniority: v })}
                      placeholder="Any level"
                      hideSelectAll
                      maxCount={4}
                      // let the trigger grow to fit wrapped chips instead of
                      // overflowing the fixed-height (h-9) box when 2+ are picked
                      className="h-auto min-h-9"
                    />
                  </Field>
                  <Field label="Location">
                    <Input
                      ref={locationRef}
                      aria-label="Location"
                      aria-describedby="job-location-help"
                      placeholder={
                        hasOfficeWork(role.locationTypes)
                          ? 'Prague, Czechia'
                          : 'Germany, Austria, Switzerland'
                      }
                      value={role.location}
                      onChange={(e) => patch({ location: e.target.value })}
                    />
                    <p
                      id="job-location-help"
                      className="text-muted-foreground text-xs"
                    >
                      Remote: where a hire may work. Onsite or hybrid: enter the
                      office location.
                    </p>
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Work arrangement">
                    <MultiSelect
                      options={locationTypeOptions}
                      defaultValue={role.locationTypes}
                      onValueChange={(v) =>
                        patch({ locationTypes: v.filter(isJobLocationType) })
                      }
                      placeholder="Onsite / Hybrid / Remote"
                      hideSelectAll
                      maxCount={3}
                      className="h-auto min-h-9"
                    />
                  </Field>
                  <Field label="Employment type">
                    <MultiSelect
                      options={contractTypeOptions}
                      defaultValue={role.contractTypes}
                      onValueChange={(v) =>
                        patch({ contractTypes: v.filter(isJobContractType) })
                      }
                      placeholder="Employment / B2B"
                      hideSelectAll
                      maxCount={2}
                      className="h-auto min-h-9"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Countries">
                    <JobCountriesField
                      countries={role.countries}
                      isAnywhere={role.isAnywhere}
                      locationTypes={role.locationTypes}
                      onChange={patch}
                      helpClassName="text-muted-foreground text-xs"
                    />
                  </Field>
                  {hasOfficeWork(role.locationTypes) && (
                    <Field label="Office city">
                      <Input
                        aria-label="Office city"
                        aria-describedby="job-office-city-help"
                        placeholder="Prague"
                        value={role.targetCity}
                        onChange={(e) => patch({ targetCity: e.target.value })}
                      />
                      <p
                        id="job-office-city-help"
                        className="text-muted-foreground text-xs"
                      >
                        Optional. Add a city when the role has a single office.
                      </p>
                    </Field>
                  )}
                </div>

                <Field label="Extra search titles">
                  <JobSearchTitlesField
                    value={role.extraSearchTitles}
                    onChange={(titles) => patch({ extraSearchTitles: titles })}
                    helpClassName="text-muted-foreground text-xs"
                  />
                </Field>

                <Field label="Candidates' company size">
                  <JobEmployerSizeField
                    value={employerSize}
                    suggested={
                      role.employerSize === undefined &&
                      suggestedEmployerSize != null
                    }
                    onChange={(band) => patch({ employerSize: band })}
                    helpClassName="text-muted-foreground text-xs"
                  />
                </Field>

                <Field label="Candidates' industries">
                  <JobEmployerIndustriesField
                    value={employerIndustries}
                    suggested={
                      role.employerIndustries === undefined &&
                      Boolean(suggestedEmployerIndustries?.length)
                    }
                    onChange={(industries) =>
                      patch({ employerIndustries: industries })
                    }
                    helpClassName="text-muted-foreground text-xs"
                  />
                </Field>

                <Field
                  label="Example companies"
                  hint="Optional. Companies known for great people in this role."
                >
                  <Input
                    ref={companiesRef}
                    placeholder="e.g. Stripe, Datadog"
                    value={role.companies}
                    onChange={(e) => patch({ companies: e.target.value })}
                    onBlur={(e) => setCheckedCompanies(e.target.value.trim())}
                  />
                  {checkedCompanies === role.companies.trim() ? (
                    <ExampleCompaniesNote
                      companies={checkedCompanies}
                      employerSize={employerSize}
                      employerIndustries={employerIndustries}
                      companyId={effectiveWorkflow.companyId}
                      className="text-muted-foreground text-xs"
                    />
                  ) : null}
                </Field>

                <Field
                  label="A great hire looks like…"
                  hint="Optional. Paste someone who'd be perfect — it tells us a lot."
                >
                  <Input
                    placeholder="Paste a LinkedIn URL"
                    value={role.exemplarUrl}
                    onChange={(e) => patch({ exemplarUrl: e.target.value })}
                  />
                </Field>
              </CardContent>
            </Card>

            {isRecruiterVariant ? (
              <WorkflowSettingsCard
                value={effectiveWorkflow}
                onChange={patchWorkflow}
                companyOptions={companyOptions}
                // create-only: the update request has no companyId, so an edit-mode picker would be a dead
                // control that also clears/refilters the tenant-scoped coordination picks
                showCompanyPicker={
                  mode === 'create' && companyOptions.length > 1
                }
                mode={mode}
                hasCoordinationProcess={hasCoordinationProcess}
                // in edit mode canPublish ⇔ the job is still a Draft
                isDraft={mode === 'edit' && canPublish}
                legacySourcingStrategy={legacySourcingStrategy}
              />
            ) : null}
            <Card>
              <CardContent>
                <JobSlackChannelField
                  value={workflow.slackChannelId ?? null}
                  currentName={workflow.slackChannelName}
                  onChange={(slackChannelId) =>
                    patchWorkflow({ slackChannelId })
                  }
                />
              </CardContent>
            </Card>
          </div>

          {/* ── Right: the live signal panel (sticky) ────────────────────── */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            <Card className="gap-0 py-5">
              <CardContent className="space-y-6">
                <CompletenessBar
                  completeness={completeness}
                  error={jobSignalQuery.isError}
                />
                <div className="border-t" />
                <NeedsSection needs={needs} onFocus={focusNeed} />
                <div className="border-t" />
                <SkillHelpSection
                  verdicts={verdicts}
                  pendingCount={pendingCount}
                  failedSkills={failedSkills}
                  sufficiencyLevel={sufficiencyLevel}
                  elicitSkill={elicitSkill}
                  onOpenChange={(input, open) =>
                    setElicitSkill(open ? input : null)
                  }
                  onResolve={resolveSkill}
                  onRetry={lintSkills}
                />
                <div className="border-t" />
                <ScreeningQuestions
                  questions={role.screeningQuestions}
                  onRemove={removeQuestion}
                  onSave={saveQuestion}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── skill entry (live typeahead + chips) ─────────────────────────────────── */

/** Owns the search query for one typeahead instance and feeds it live `/skill-search` results. */
function SkillSearchInput({
  onCommit,
  placeholder,
}: {
  onCommit: (raw: string) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const { data: results = [], isFetching } = useSkillSearchQuery(query);

  return (
    <SkillTypeahead
      // Below the query threshold `keepPreviousData` would surface the prior query's stale hits — clear them.
      results={query.trim().length >= 2 ? results : []}
      isLoading={isFetching}
      onQueryChange={setQuery}
      onSelect={onCommit}
      onFreeTextCommit={onCommit}
      placeholder={placeholder}
    />
  );
}

function SkillChips({
  skills,
  verdictCache,
  onRemove,
  emphasis,
  onEmphasis,
}: {
  skills: string[];
  /** When present, each chip shows its live verdict dot; absent = plain chips (nice-to-have). */
  verdictCache?: Record<string, SkillVerdict>;
  onRemove: (skill: string) => void;
  /** How much the search leans on each skill, with an arrow on each chip to change it. */
  emphasis: Record<string, EmphasisLevel>;
  onEmphasis: (skill: string, level: EmphasisLevel | 'Normal') => void;
}) {
  if (skills.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {skills.map((skill) => {
        const verdict = verdictCache?.[normalize(skill)];
        return (
          <span
            key={skill}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border py-1 pr-1 pl-2.5 text-sm',
              verdict
                ? verdictChipClass(verdict.verdict)
                : 'border-neutral-400',
            )}
          >
            {verdictCache ? (
              verdict ? (
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    verdictDot(verdict.verdict),
                  )}
                />
              ) : (
                <span className="size-1.5 animate-pulse rounded-full bg-neutral-500" />
              )
            ) : null}
            <span className="text-foreground">
              {verdict ? displayLabel(verdict) : skill}
            </span>
            <SkillEmphasisMenu
              skill={verdict ? displayLabel(verdict) : skill}
              level={emphasisOf(emphasis, skill)}
              onChange={(level) => onEmphasis(skill, level)}
            />
            <button
              type="button"
              onClick={() => onRemove(skill)}
              aria-label={`Remove ${skill}`}
              className="text-neutral-650 hover:text-foreground rounded-full p-0.5"
            >
              <X className="size-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
}

/* ── shared bits ────────────────────────────────────────────────────────── */

function StrengthBar({ value }: { value: number }) {
  const color =
    value >= 0.75
      ? 'bg-semantic-560'
      : value >= 0.45
        ? 'bg-semantic-540'
        : 'bg-neutral-400';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-300">
      <div
        className={cn('h-full rounded-full transition-all duration-500', color)}
        style={{ width: `${Math.round(value * 100)}%` }}
      />
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-foreground block text-sm font-medium">{label}</span>
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      {children}
    </div>
  );
}

function SectionHeader({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="text-primary-700 flex items-center gap-2 [&_svg]:size-4">
      {icon}
      <h3 className="font-medium">{title}</h3>
    </div>
  );
}

/* ── health bar ───────────────────────────────────────────────────────────── */

function CompletenessBar({
  completeness,
  error,
}: {
  completeness: Completeness | undefined;
  error: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-foreground text-sm font-medium">
          Signal strength
        </span>
        <span className="text-muted-foreground text-xs">
          {completeness ? completenessLabelText(completeness.label) : '…'}
        </span>
      </div>
      <div className="mt-2">
        <StrengthBar value={completeness?.strength ?? 0} />
      </div>
      {!completeness ? (
        <p className="text-muted-foreground mt-1.5 text-xs">
          {error ? "Couldn't check the signal right now." : 'Checking…'}
        </p>
      ) : completeness.nextStep ? (
        <p className="text-muted-foreground mt-1.5 text-xs">
          Next:{' '}
          <span className="text-foreground font-medium">
            {nextStepCopy(completeness.nextStep)}
          </span>
        </p>
      ) : completeness.label === 'strong' ? (
        <p className="text-semantic-560 mt-1.5 text-xs font-medium">
          Strong — ready to publish.
        </p>
      ) : null}
    </div>
  );
}

/* ── 1. What we need to know ──────────────────────────────────────────────── */

function NeedsSection({
  needs,
  onFocus,
}: {
  needs: NeedItem[];
  onFocus: (id: NeedItem['id']) => void;
}) {
  return (
    <div>
      <SectionHeader icon={<ListChecks />} title="What we need to know" />
      <p className="text-muted-foreground mt-0.5 text-xs">
        The more you give us, the sharper the search.
      </p>
      <ul className="mt-3 space-y-2">
        {needs.map((need) => (
          <NeedRow key={need.id} need={need} onFocus={onFocus} />
        ))}
      </ul>
    </div>
  );
}

function NeedRow({
  need,
  onFocus,
}: {
  need: NeedItem;
  onFocus: (id: NeedItem['id']) => void;
}) {
  if (need.status === 'done') {
    return (
      <li className="flex items-center gap-2 text-sm">
        <Check className="text-semantic-560 size-4 shrink-0" />
        <span className="text-muted-foreground">{need.label}</span>
      </li>
    );
  }

  const optional = need.status === 'optional';
  return (
    <li>
      <button
        type="button"
        onClick={() => onFocus(need.id)}
        className={cn(
          'flex w-full items-start gap-2 rounded-lg border p-2.5 text-left transition-colors',
          optional
            ? 'border-dashed border-neutral-400 hover:bg-neutral-200'
            : 'border-semantic-540/50 bg-semantic-540/10 hover:bg-semantic-540/20',
        )}
      >
        {optional ? (
          <Plus className="text-neutral-650 mt-0.5 size-4 shrink-0" />
        ) : (
          <AlertTriangle className="text-semantic-540 mt-0.5 size-4 shrink-0" />
        )}
        <span className="flex-1">
          <span className="text-foreground block text-sm font-medium">
            {need.label}
            {optional ? ' (optional)' : ''}
          </span>
          <span className="text-muted-foreground block text-xs">
            {need.hint}
          </span>
        </span>
        <span className="text-primary shrink-0 text-xs font-semibold">Add</span>
      </button>
    </li>
  );
}

/* ── 2. Help us look for an ideal candidate ───────────────────────────────── */

function SkillHelpSection({
  verdicts,
  pendingCount,
  failedSkills,
  sufficiencyLevel,
  elicitSkill,
  onOpenChange,
  onResolve,
  onRetry,
}: {
  verdicts: SkillVerdict[];
  pendingCount: number;
  failedSkills: string[];
  sufficiencyLevel: SufficiencyLevel;
  elicitSkill: string | null;
  onOpenChange: (input: string, open: boolean) => void;
  onResolve: (
    verdict: SkillVerdict,
    action: ResolveAction,
    value: string,
  ) => void;
  onRetry: (skills: string[]) => void;
}) {
  const strong = verdicts.filter((v) => isStrongVerdict(v.verdict));
  const flagged = verdicts.filter((v) => !isStrongVerdict(v.verdict));

  return (
    <div>
      <SectionHeader
        icon={<Search />}
        title="Help us look for an ideal candidate"
      />
      <p className="text-muted-foreground mt-0.5 text-xs">
        Skills we can search for — and ones we can&apos;t.
      </p>
      {verdicts.length === 0 &&
      pendingCount === 0 &&
      failedSkills.length === 0 ? (
        <p className="text-muted-foreground mt-3 text-sm">
          Add must-have skills and we&apos;ll check we can search for them.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {strong.map((v) => (
            <div key={v.input} className="flex items-center gap-2 text-sm">
              <Check className="text-semantic-560 size-4 shrink-0" />
              <span className="text-foreground">{displayLabel(v)}</span>
              <span className="text-muted-foreground text-xs">
                good for search
              </span>
            </div>
          ))}
          {flagged.map((v) => (
            <FlaggedSkill
              key={v.input}
              verdict={v}
              open={elicitSkill === v.input}
              onOpenChange={(open) => onOpenChange(v.input, open)}
              onResolve={(action, value) => onResolve(v, action, value)}
            />
          ))}
          {pendingCount > 0 ? (
            <p className="text-muted-foreground text-xs">
              Checking {pendingCount} skill{pendingCount === 1 ? '' : 's'}…
            </p>
          ) : null}
          {failedSkills.length > 0 ? (
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-destructive">
                Couldn&apos;t check {failedSkills.length} skill
                {failedSkills.length === 1 ? '' : 's'}.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRetry(failedSkills)}
              >
                Try again
              </Button>
            </div>
          ) : null}
          {verdicts.length > 0 ? (
            <SufficiencyNote
              level={sufficiencyLevel}
              strongLabels={strong.map(displayLabel)}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function SufficiencyNote({
  level,
  strongLabels,
}: {
  level: SufficiencyLevel;
  strongLabels: string[];
}) {
  const message = skillSufficiencyMessage(level, strongLabels);
  if (level === 'ok') {
    return (
      <div className="text-semantic-560 flex items-center gap-2 text-xs">
        <Check className="size-3.5 shrink-0" />
        <span>{message}</span>
      </div>
    );
  }
  return (
    <div className="border-semantic-540/50 bg-semantic-540/10 flex items-start gap-2 rounded-lg border p-2.5">
      <AlertTriangle className="text-semantic-540 mt-0.5 size-4 shrink-0" />
      <span className="text-foreground text-xs">{message}</span>
    </div>
  );
}

const flaggedReason = (verdict: SkillVerdict): string =>
  verdict.verdict === 'ubiquitous'
    ? 'Too broad — it describes almost everyone.'
    : verdict.verdict === 'vague'
      ? "Too vague — it's a trait, not a searchable skill."
      : "We don't recognise this one.";

function FlaggedSkill({
  verdict,
  open,
  onOpenChange,
  onResolve,
}: {
  verdict: SkillVerdict;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolve: (action: ResolveAction, value: string) => void;
}) {
  return (
    <div className="border-semantic-540/50 bg-semantic-540/10 rounded-lg border p-2.5">
      <div className="flex items-start gap-2">
        <AlertTriangle className="text-semantic-540 mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-foreground block text-sm font-medium">
            {verdict.input}
          </span>
          <span className="text-muted-foreground block text-xs">
            {flaggedReason(verdict)}
          </span>
        </div>
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="warning" className="shrink-0">
              Fix
            </Button>
          </PopoverTrigger>
          <SkillElicitation verdict={verdict} onResolve={onResolve} />
        </Popover>
      </div>
    </div>
  );
}

function SkillElicitation({
  verdict,
  onResolve,
}: {
  verdict: SkillVerdict;
  onResolve: (action: ResolveAction, value: string) => void;
}) {
  const [answer, setAnswer] = useState('');
  const kind = verdictElicitation(verdict.verdict);
  const isRecover = kind === 'recover'; // unknown

  const title = isRecover
    ? `We don't recognise “${verdict.input}”.`
    : verdict.verdict === 'ubiquitous'
      ? `“${verdict.input}” matches almost everyone.`
      : `“${verdict.input}” is a bit vague.`;
  const body = isRecover
    ? "What's the closest public tool or skill? We'll search for that instead."
    : "What do you actually need? We'll turn it into a question to ask in the interview.";
  const action: ResolveAction = isRecover ? 'replace' : 'toQuestion';
  const cta = isRecover ? 'Use this' : 'Make it a question';
  const placeholder = isRecover
    ? 'e.g. Redux'
    : 'e.g. mentoring junior engineers';

  return (
    <PopoverContent align="end" className="w-80">
      <p className="text-foreground text-sm font-medium">{title}</p>
      <p className="text-muted-foreground mt-1 text-sm">{body}</p>
      <div className="mt-3 flex gap-2">
        <Input
          autoFocus
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={placeholder}
        />
        <Button
          size="sm"
          disabled={!answer.trim()}
          onClick={() => onResolve(action, answer)}
        >
          {cta}
        </Button>
      </div>
    </PopoverContent>
  );
}

/* ── 3. Screening questions ───────────────────────────────────────────────── */

function ScreeningQuestions({
  questions,
  onRemove,
  onSave,
}: {
  questions: string[];
  onRemove: (index: number) => void;
  onSave: (index: number, text: string) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  return (
    <div>
      <div className="flex items-center justify-between">
        <SectionHeader icon={<HelpCircle />} title="Screening questions" />
        {questions.length ? (
          <Badge variant="secondary" size="sm">
            {questions.length}
          </Badge>
        ) : null}
      </div>
      <p className="text-muted-foreground mt-0.5 text-xs">
        We&apos;ll ask these during an interview with the candidate.
      </p>
      {questions.length === 0 ? (
        <p className="text-muted-foreground mt-3 text-sm">
          Fix a “too broad” skill into a question and it&apos;ll show up here.
        </p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {questions.map((q, i) =>
            editingIndex === i ? (
              <QuestionEditor
                key={i}
                initial={q}
                onCancel={() => setEditingIndex(null)}
                onSave={(text) => {
                  onSave(i, text);
                  setEditingIndex(null);
                }}
              />
            ) : (
              <QuestionRow
                key={i}
                question={q}
                onEdit={() => setEditingIndex(i)}
                onRemove={() => onRemove(i)}
              />
            ),
          )}
        </ul>
      )}
    </div>
  );
}

function QuestionRow({
  question,
  onEdit,
  onRemove,
}: {
  question: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="group flex items-start gap-2 text-sm">
      <HelpCircle className="text-primary mt-0.5 size-3.5 shrink-0" />
      <span className="text-foreground flex-1">{question}</span>
      <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit question"
          className="text-neutral-650 hover:text-foreground rounded p-0.5"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove question"
          className="text-neutral-650 hover:text-foreground rounded p-0.5"
        >
          <X className="size-3.5" />
        </button>
      </span>
    </li>
  );
}

function QuestionEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const commit = () => {
    const trimmed = text.trim();
    if (trimmed) onSave(trimmed);
    else onCancel();
  };
  return (
    <li className="flex items-center gap-2">
      <Input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button size="sm" onClick={commit}>
        Save
      </Button>
    </li>
  );
}
